import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@/contexts/ThemeContext';
import { useUserContext, canAccessView } from '@/contexts/UserContext';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { LanguageMenu } from '@/components/common/LanguageMenu';

export type ViewId = 'survey' | 'safe' | 'admin' | 'executive';

interface SidebarProps {
  activeView: ViewId;
  onViewChange: (view: ViewId) => void;
}

const NAV_LINKS: Array<{ view: ViewId; icon: string; labelKey: string }> = [
  { view: 'survey',    icon: 'fa-clipboard-list', labelKey: 'nav.home'      },
  { view: 'safe',      icon: 'fa-seedling',        labelKey: 'nav.growth'    },
  { view: 'admin',     icon: 'fa-cog',             labelKey: 'nav.admin'     },
  { view: 'executive', icon: 'fa-users',            labelKey: 'nav.executive' },
];

function getInitialsFromName(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map(p => p[0]?.toUpperCase() ?? '')
    .join('');
}

export function Sidebar({ activeView, onViewChange }: SidebarProps) {
  const { t } = useTranslation();
  const { theme, toggle } = useThemeContext();
  const { demoUser } = useUserContext();
  const currentUser = useCurrentUser();

  // Prefer MSAL user when signed in; fall back to demo user profile
  const initials = currentUser?.initials
    ?? (demoUser.nombre ? getInitialsFromName(demoUser.nombre) : '??');

  const activeRole = currentUser?.primaryRole ?? demoUser.role;

  const roleDisplayLabel = {
    Employee:    t('roles.employee'),
    AdminOU:     t('roles.adminOU'),
    AdminGlobal: t('roles.adminGlobal'),
  }[activeRole];

  // Only show nav items accessible for the current role
  const visibleLinks = NAV_LINKS.filter(({ view }) => canAccessView(view, activeRole));

  return (
    <nav className="side-nav">
      <div className="side-nav-links">
        {visibleLinks.map(({ view, icon, labelKey }) => (
          <button
            key={view}
            className={`side-link${activeView === view ? ' active' : ''}`}
            title={t(labelKey)}
            onClick={() => onViewChange(view)}
            aria-label={t(labelKey)}
            aria-current={activeView === view ? 'page' : undefined}
          >
            <i className={`fas ${icon}`} aria-hidden="true" />
            <span className="side-link-label">{t(labelKey)}</span>
          </button>
        ))}

        <LanguageMenu />

        <button
          className="dark-toggle"
          title={theme === 'dark' ? t('common.lightMode') : t('common.darkMode')}
          onClick={toggle}
          aria-label={theme === 'dark' ? t('common.lightMode') : t('common.darkMode')}
        >
          <i className={`fas ${theme === 'dark' ? 'fa-sun' : 'fa-moon'}`} aria-hidden="true" />
        </button>
      </div>

      <div className="side-nav-bottom">
        <div className="side-avatar" aria-label={currentUser?.displayName ?? (demoUser.nombre || t('roles.employee'))}>
          {initials}
        </div>
        <div className="side-role-label">{roleDisplayLabel}</div>
      </div>
    </nav>
  );
}
